import React, { type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'

import { Box } from '../box'
import { HStack } from '../hstack'
import { Text } from '../text'
import { VStack } from '../vstack'

export type DetailsListEntry = {
    label: string
    value: ReactNode
}

export type DetailsListTone = 'default' | 'on-graphics'

export type DetailsListItemProps = {
    label: string
    children?: ReactNode
    /** Value ink. Use `on-graphics` when the list sits on a light ground. */
    tone?: DetailsListTone
    className?: string
}

// Figma "DetailsListItem" (node 9637:83074): P1 label in content/muted over its
// value, 4px apart. Plain values are wrapped so callers can pass a bare string.
export const DetailsListItem = ({
    label,
    children,
    tone = 'default',
    className,
}: DetailsListItemProps) => (
    <VStack className={twMerge('gap-1', className)}>
        <Text size='body' tone={tone === 'on-graphics' ? 'on-graphics' : 'muted'}>
            {label}
        </Text>
        {typeof children === 'string' || typeof children === 'number' ? (
            <Text size='body' tone={tone}>
                {children}
            </Text>
        ) : (
            children
        )}
    </VStack>
)

DetailsListItem.displayName = 'DetailsListItem'

export type DetailsListProps = {
    items: DetailsListEntry[]
    columns?: number
    tone?: DetailsListTone
    className?: string
}

const chunk = <T,>(items: T[], size: number): T[][] => {
    const rows: T[][] = []
    for (let i = 0; i < items.length; i += size) {
        rows.push(items.slice(i, i + size))
    }
    return rows
}

/**
 * Read-only label/value grid for detail panels. Rows are built explicitly rather
 * than with CSS grid so the same component lays out on native.
 */
export const DetailsList = ({ items, columns = 1, tone, className }: DetailsListProps) => {
    const perRow = Math.max(1, Math.floor(columns))

    return (
        <VStack className={twMerge('gap-3', className)}>
            {chunk(items, perRow).map((row, rowIndex) => (
                <HStack key={rowIndex} className='items-start gap-3'>
                    {row.map((entry, index) => (
                        <Box key={`${entry.label}-${index}`} className='min-w-0 flex-1'>
                            <DetailsListItem label={entry.label} tone={tone}>
                                {entry.value}
                            </DetailsListItem>
                        </Box>
                    ))}
                    {Array.from({ length: perRow - row.length }, (_, index) => (
                        <Box key={`filler-${index}`} className='min-w-0 flex-1' />
                    ))}
                </HStack>
            ))}
        </VStack>
    )
}

DetailsList.displayName = 'DetailsList'
